import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

// Basic Auth credentials
const AUTH_USERNAME = 'user';
const AUTH_PASSWORD = 'Admin@123';

// API endpoints
const ROLES_API = 'http://127.0.0.1:8000/api/roles/';
const PERMISSIONS_API = 'http://127.0.0.1:8000/api/permissions/';

// Axios instances
const apiRoles = axios.create({
  baseURL: ROLES_API,
  auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
});
const apiPermissions = axios.create({
  baseURL: PERMISSIONS_API,
  auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
});

export default function PermissionsPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    role: '',
    can_create: false,
    can_read: false,
    can_update: false,
    can_delete: false,
  });

  useEffect(() => setPage(0), [permissions.length]);
  const paginatedPermissions = permissions.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const handleChangePage = (event, nextPage) => setPage(nextPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  // Load roles & permissions
  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await apiRoles.get('');
      setRoles(res.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await apiPermissions.get('');
      setPermissions(res.data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  const handleOpen = (perm = null) => {
    if (perm) {
      setFormData(perm);
      setIsEditing(true);
    } else {
      setFormData({
        id: null,
        role: '',
        can_create: false,
        can_read: false,
        can_update: false,
        can_delete: false,
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await apiPermissions.put(`${formData.id}/`, formData);
      } else {
        await apiPermissions.post('', formData);
      }
      fetchPermissions();
      handleClose();
    } catch (err) {
      console.error('Error saving permission:', err);
    }
  };

  // Helper to get role name
  const getRoleName = (id) => {
    const r = roles.find((r) => r.id === id);
    return r ? r.name : '—';
  };

  return (
    <Box p={3}>
      {/* Breadcrumb & Add */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Breadcrumbs aria-label="breadcrumb">
         <Link color="inherit" href="/dashboard">Dashboard</Link>
          <Typography color="text.primary">Permissions</Typography> 
        </Breadcrumbs>
        <Button variant="contained" onClick={() => handleOpen()}>
          Add Permission
        </Button>
      </Box>

      {/* Permissions Table */}
      <TableContainer>
        <Table className="global-table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Create</TableCell>
              <TableCell>Read</TableCell>
              <TableCell>Update</TableCell>
              <TableCell>Delete</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPermissions.map((perm) => (
              <TableRow key={perm.id}>
                <TableCell>{perm.id}</TableCell>
                <TableCell>{getRoleName(perm.role)}</TableCell>
                <TableCell>{perm.can_create ? '✔️' : '✖️'}</TableCell>
                <TableCell>{perm.can_read ? '✔️' : '✖️'}</TableCell>
                <TableCell>{perm.can_update ? '✔️' : '✖️'}</TableCell>
                <TableCell>{perm.can_delete ? '✔️' : '✖️'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpen(perm)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={permissions.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Dialog for Add/Edit */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit Permission' : 'Add Permission'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* Role Select */}
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleChange}
              >
                {roles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* CRUD Checkboxes */}
            <FormControlLabel
              control={
                <Checkbox
                  name="can_create"
                  checked={formData.can_create}
                  onChange={handleChange}
                />
              }
              label="Can Create"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="can_read"
                  checked={formData.can_read}
                  onChange={handleChange}
                />
              }
              label="Can Read"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="can_update"
                  checked={formData.can_update}
                  onChange={handleChange}
                />
              }
              label="Can Update"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="can_delete"
                  checked={formData.can_delete}
                  onChange={handleChange}
                />
              }
              label="Can Delete"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
